import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { useActivityHead } from "@/hooks/use-activity-head";
import { useTranslation } from "react-i18next";

// High-water mark: the newest event `at` the user has acknowledged, per brand.
// localStorage access is guarded — private mode / blocked storage degrades to
// "dot always shows", never a crash.
function readSeen(brandId: string): string | null {
  try {
    return localStorage.getItem(`activity-seen:${brandId}`);
  } catch {
    return null;
  }
}

function writeSeen(brandId: string, at: string) {
  try {
    localStorage.setItem(`activity-seen:${brandId}`, at);
  } catch {
    // private mode — the dot will simply reappear next session
  }
}

interface ActivityBellProps {
  brandId?: string | null;
  brandSlug?: string | null;
}

/** Topbar bell — only rendered inside a brand context. Shows a dot when the
 *  feed head is newer than the stored high-water mark (ISO string compare is
 *  safe for equal-precision timestamps); clicking marks seen and navigates to
 *  the activity page. */
export function ActivityBell({ brandId, brandSlug }: ActivityBellProps) {
  const navigate = useNavigate();
  const head = useActivityHead(brandId);
  const [seen, setSeen] = useState<string | null>(() => (brandId ? readSeen(brandId) : null));
  const { t } = useTranslation("nav");

  // Re-read the mark when switching brands.
  useEffect(() => {
    setSeen(brandId ? readSeen(brandId) : null);
  }, [brandId]);

  if (!brandId || !brandSlug) return null;

  const headAt = head.data?.events?.[0]?.at ?? null;
  const showDot = !!headAt && (seen === null || headAt > seen);

  const handleClick = () => {
    if (headAt) {
      writeSeen(brandId, headAt);
      setSeen(headAt);
    }
    navigate(`/${brandSlug}/activity`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={t("activity")}
      aria-label={t("activity")}
      className="relative p-1 text-muted-foreground hover:text-foreground transition-colors"
    >
      <Bell size={15} />
      {showDot && (
        <span
          data-testid="activity-dot"
          aria-hidden="true"
          className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-primary"
        />
      )}
    </button>
  );
}
